/**
 * Sentry Error Tracking Integration
 *
 * Provides centralized error tracking with:
 * - Correlation ID tracking
 * - Context-aware error reporting
 * - Automatic error detection for known bugs
 * - enableLogs + consoleLoggingIntegration (console.log/warn/error → Sentry)
 * - startSpan för tracing (UI/API)
 *
 * Använd: captureException i try/catch, startSpan kring viktiga actions/API-anrop.
 */

interface SentryConfig {
  dsn?: string;
  environment: string;
  release?: string;
}

/** Error-like shape for healthcheck/404 filtering (SDK or custom). */
type ErrorLike = { code?: string; status?: number; response?: { status?: number; code?: string }; filename?: string; stack?: string };
/** Event.extra shape for filtering. */
type EventExtraLike = { functionName?: string; filename?: string; error_type?: string; message?: string; code?: string; has_blank_screen?: boolean };
/** Event.contexts.runtime shape. */
type EventContextRuntime = { name?: string; type?: string };
/** Event.contexts.error shape. */
type EventContextError = { filename?: string; type?: string };

export type SentryStatus = "idle" | "ready" | "no_dsn" | "error";

let sentryStatus: SentryStatus = "idle";
let sentryInitialized = false;
/** Lagrad referens till @sentry/react så captureException/captureMessage/startSpan fungerar (window.Sentry sätts inte av SDK). */
let sentryModule: typeof import("@sentry/react") | null = null;

/** Events captured before init completes – flushed when Sentry is ready so we never drop early errors. */
type PendingContext = {
  correlationId?: string;
  tags?: Record<string, string>;
  extra?: Record<string, unknown>;
};
type PendingEvent =
  | { type: "exception"; error: Error; context?: PendingContext }
  | { type: "message"; message: string; level: "info" | "warning" | "error"; context?: PendingContext };
const pendingEvents: PendingEvent[] = [];
const MAX_PENDING = 50;

const isTestEnv =
  import.meta.env.MODE === "test" ||
  import.meta.env.VITEST === true ||
  (typeof process !== "undefined" && process.env.NODE_ENV === "test");

export function getSentryStatus(): SentryStatus {
  return sentryStatus;
}

export function initSentry(config: SentryConfig) {
  if (isTestEnv) {
    sentryStatus = "no_dsn";
    return;
  }
  const dsn = config.dsn && String(config.dsn).trim() ? config.dsn : undefined;
  if (!dsn) {
    sentryStatus = "no_dsn";
    if (import.meta.env.DEV) {
      console.warn("[Sentry] No DSN provided - Sentry will not initialize");
    }
    return;
  }
  if (sentryInitialized) {
    if (import.meta.env.DEV) {
      console.log("[Sentry] Already initialized, skipping");
    }
    return;
  }

  // Initiera Sentry om DSN finns (i prod eller preview - Lovable preview kan ha DSN)
  if (typeof window !== "undefined") {
    if (import.meta.env.DEV) {
      console.log("[Sentry] Initializing with DSN:", dsn.substring(0, 30) + "...", "env:", config.environment);
    }
    import(/* @vite-ignore */ "@sentry/react")
      .then((S) => {
        S.init({
          dsn,
          environment: config.environment,
          release: config.release,
          enableLogs: true,
          integrations: (existing) => [
            ...existing,
            ...(typeof S.consoleLoggingIntegration === "function"
              ? [S.consoleLoggingIntegration({ levels: ["log", "warn", "error"] })]
              : []),
          ],
          beforeSend(event, hint) {
          // Lovable-fix: event.message kan vara string | Primitive – inte .value/.formatted.
          // Try-catch så beforeSend aldrig kastar (vit skärm).
          const getMsg = (m: unknown): string => {
            try {
              if (typeof m === 'string') return m;
              if (m != null && typeof m === 'object') {
                const o = m as Record<string, unknown>;
                return String(o.value ?? o.formatted ?? '');
              }
              return String(m ?? '');
            } catch {
              return '';
            }
          };
          
          // CRITICAL: Always allow test errors from SentryVerify (explicit allow)
          const tags = event.tags || {};
          const error = hint.originalException;
          if (error instanceof Error && error.message === "This is your first error!") {
            // Test error from SentryVerify - always send it
            if (import.meta.env.DEV) {
              console.log("[Sentry] Allowing test error:", error.message, tags);
            }
            return event;
          }
          if (tags.source === "sentry_verify_button" || tags.source === "sentry_verify_render") {
            // Test error from SentryVerify - always send it
            if (import.meta.env.DEV) {
              console.log("[Sentry] Allowing test error with tag:", tags.source);
            }
            return event;
          }
          
          // Filter out known non-critical errors
          if (event.exception) {
            if (error instanceof Error) {
              // Don't send network errors (user's connection issue)
              if (error.message.includes('Failed to fetch') || 
                  error.message.includes('NetworkError')) {
                return null;
              }
              
              // Do not send healthcheck 404 (function is optional).
              // FAANG-LEVEL: Comprehensive filtering to prevent blank screens
              // Check error message, filename, stack trace, and all error properties
              const errorMessage = error.message || '';
              const errorStack = error.stack || '';
              const filename = (event.exception.values?.[0]?.stacktrace?.frames?.[0]?.filename) || '';
              const errorObj = error as ErrorLike;
              
              // Check all possible 404 indicators
              const has404 = 
                errorMessage.includes('404') || 
                errorMessage.includes('NOT_FOUND') ||
                errorMessage.includes('not found') ||
                errorMessage.includes('Requested function was not found') ||
                errorMessage.includes('Edge function returned 404') ||
                errorObj?.code === 'NOT_FOUND' ||
                errorObj?.status === 404 ||
                errorObj?.response?.status === 404 ||
                errorObj?.response?.code === 'NOT_FOUND';
              
              // Check all possible healthcheck indicators
              const isHealthcheck = 
                errorMessage.includes('healthcheck') || 
                filename.includes('healthcheck') || 
                filename.includes('healthcheck/index.ts') ||
                filename.includes('functions/healthcheck') ||
                errorStack.includes('healthcheck') ||
                errorObj?.filename?.includes('healthcheck') ||
                errorObj?.stack?.includes('healthcheck');
              
              // If it's a healthcheck 404 error, ignore it completely
              if (isHealthcheck && has404) {
                return null;
              }
              
              // Filter out healthcheck-related errors from tags/extra
              const tags = event.tags || {};
              const extra = event.extra || {};
              if (tags.functionName === 'healthcheck' || 
                  (extra as EventExtraLike).functionName === 'healthcheck' ||
                  String(tags.functionName ?? '').includes('healthcheck')) {
                return null;
              }
            }
          }
          
          // Also check message/context for healthcheck references
          if (event.message) {
            const message = getMsg(event.message);
            if ((message.includes('healthcheck') || message.includes('healthcheck/index.ts')) && 
                (message.includes('404') || message.includes('NOT_FOUND'))) {
              return null;
            }
          }
          
          // Check filename in exception frames to identify error source.
          // Error comes from "supabase/functions/healthcheck/index.ts" - check filename in frames
          // FAANG-LEVEL: Also check event.extra and event.contexts for filename
          if (event.exception?.values) {
            for (const exception of event.exception.values) {
              // Check exception value/message
              const exceptionValue = exception.value || '';
              const has404 = exceptionValue.includes('404') || 
                            exceptionValue.includes('NOT_FOUND') ||
                            exceptionValue.includes('not found') ||
                            exceptionValue.includes('Requested function was not found') ||
                            exceptionValue.includes('Edge function returned 404');
              
              // Check filename in stacktrace frames
              if (exception.stacktrace?.frames) {
                for (const frame of exception.stacktrace.frames) {
                  if (frame.filename) {
                    const filename = frame.filename;
                    // If filename contains healthcheck AND it's a 404 error, ignore it
                    if ((filename.includes('healthcheck') || 
                         filename.includes('healthcheck/index.ts') ||
                         filename.includes('functions/healthcheck')) &&
                        has404) {
                      return null;
                    }
                  }
                }
              }
              
              // Also check if exception value mentions healthcheck and 404
              if (has404 && 
                  (exceptionValue.includes('healthcheck') || 
                   exceptionValue.includes('healthcheck/index.ts'))) {
                return null;
              }
            }
          }
          
          // Also check event context/extra for filename.
          // CRITICAL: Check event.extra.filename which may contain "supabase/functions/healthcheck/index.ts"
          const extra = event.extra || {};
          const context = event.contexts || {};
          const extraFilename = (extra as EventExtraLike).filename || '';
          const contextFilename = (context as { runtime?: EventContextRuntime }).runtime?.name || '';
          const allFilenames = [extraFilename, contextFilename].filter(Boolean);
          
          for (const filename of allFilenames) {
            if (filename && 
                (filename.includes('healthcheck') || filename.includes('healthcheck/index.ts') || filename.includes('functions/healthcheck'))) {
              // Check if it's a 404 error
              const messageStr = getMsg(event.message);
              const has404InMessage = 
                messageStr.includes('404') || messageStr.includes('NOT_FOUND');
              
              if (has404InMessage) {
                return null;
              }
            }
          }
          
          // Check event.extra for structured error data.
          // Some error loggers create structured error objects with filename field
          // This handles errors with format: { timestamp, error_type: "RUNTIME_ERROR", filename, has_blank_screen, ... }
          if (extra && typeof extra === 'object') {
            const extraTyped = extra as EventExtraLike;
            const extraFilename = extraTyped.filename || '';
            const extraErrorType = extraTyped.error_type || '';
            const extraMessage = extraTyped.message || '';
            const extraCode = extraTyped.code || '';
            const extraHasBlankScreen = extraTyped.has_blank_screen;
            
            // Check if it's a healthcheck error with 404
            const isHealthcheckInExtra = 
              extraFilename.includes('healthcheck') || 
              extraFilename.includes('healthcheck/index.ts') ||
              extraFilename.includes('functions/healthcheck') ||
              extraFilename.includes('supabase/functions/healthcheck') ||
              extraMessage.includes('healthcheck');
            
            const has404InExtra = 
              extraErrorType === 'RUNTIME_ERROR' ||
              extraCode === 'NOT_FOUND' ||
              extraMessage.includes('404') ||
              extraMessage.includes('NOT_FOUND') ||
              extraMessage.includes('not found') ||
              extraMessage.includes('Requested function was not found') ||
              extraMessage.includes('Edge function returned 404') ||
              extraMessage.includes('Edge Function returned 404');
            
            // CRITICAL: If it's a healthcheck RUNTIME_ERROR with 404, ignore it completely
            // This prevents blank screens from healthcheck 404 errors
            if (isHealthcheckInExtra && (has404InExtra || extraErrorType === 'RUNTIME_ERROR')) {
              return null;
            }
          }
          
          // Also check if the error object has the structured format.
          // Some errors come as objects with { timestamp, error_type, filename, has_blank_screen }
          const originalError = hint.originalException;
          if (originalError instanceof Error) {
            const errLike = originalError as Error & EventExtraLike;
            const errorFilename = errLike.filename || '';
            const errorType = errLike.error_type || '';
            const errorMessage = originalError.message || '';
            const errorCode = errLike.code || '';
            const errorHasBlankScreen = errLike.has_blank_screen;
            
            // Check if it's a structured healthcheck error
            const isStructuredHealthcheckError = 
              (errorFilename.includes('healthcheck') || 
               errorFilename.includes('healthcheck/index.ts') ||
               errorFilename.includes('functions/healthcheck') ||
               errorFilename.includes('supabase/functions/healthcheck') ||
               errorMessage.includes('healthcheck')) &&
              (errorType === 'RUNTIME_ERROR' ||
               errorCode === 'NOT_FOUND' ||
               errorMessage.includes('404') ||
               errorMessage.includes('NOT_FOUND') ||
               errorMessage.includes('not found') ||
               errorMessage.includes('Requested function was not found') ||
               errorMessage.includes('Edge function returned 404') ||
               errorMessage.includes('Edge Function returned 404'));
            
            if (isStructuredHealthcheckError) {
              return null;
            }
          }
          
          // Also check event.tags for functionName.
          // Some error loggers set functionName in tags
          // (tags already declared above)
          if (tags.functionName === 'healthcheck' && 
              (tags.statusCode === '404' || tags.errorCode === 'NOT_FOUND' || tags.errorType === 'RUNTIME_ERROR')) {
            return null;
          }
          
          // Check event.contexts for runtime/error context.
          // Some errors have filename in contexts.runtime or contexts.error
          const contexts = event.contexts || {};
          const runtimeContext = contexts.runtime as EventContextRuntime | undefined;
          const errorContext = contexts.error as EventContextError | undefined;
          
          if (runtimeContext || errorContext) {
            const contextFilename = runtimeContext?.name || errorContext?.filename || '';
            const contextType = runtimeContext?.type || errorContext?.type || '';
            
            if (contextFilename && 
                (contextFilename.includes('healthcheck') || 
                 contextFilename.includes('healthcheck/index.ts') ||
                 contextFilename.includes('functions/healthcheck') ||
                 contextFilename.includes('supabase/functions/healthcheck'))) {
              // If it's a 404 or RUNTIME_ERROR, ignore it
              const msg = getMsg(event.message);
              if (contextType === 'RUNTIME_ERROR' || 
                  msg.includes('404') || msg.includes('NOT_FOUND')) {
                return null;
              }
            }
          }
          
          return event;
        },
        });
        sentryModule = S;
        sentryInitialized = true;
        sentryStatus = "ready";
        if (import.meta.env.DEV) {
          console.log("[Sentry] ✅ Initialized successfully, status:", sentryStatus);
        }
        // Flush events captured before init (no silent loss)
        if (pendingEvents.length > 0 && import.meta.env.PROD) {
          for (const ev of pendingEvents) {
            try {
              if (ev.type === "exception") {
                S.captureException(ev.error, {
                  tags: ev.context?.tags,
                  extra: { ...ev.context?.extra, correlationId: ev.context?.correlationId },
                });
              } else {
                S.captureMessage(ev.message, {
                  level: ev.level,
                  tags: ev.context?.tags,
                  extra: { ...ev.context?.extra, correlationId: ev.context?.correlationId },
                });
              }
            } catch (flushErr) {
              console.warn("[Sentry] Flush pending event failed:", flushErr);
            }
          }
          pendingEvents.length = 0;
        }
      })
      .catch((err) => {
        sentryStatus = "error";
        console.error("[Sentry] ❌ Failed to initialize:", err);
      });
  }
}

/**
 * Capture exception with correlation ID
 */
export function captureException(
  error: Error,
  context?: {
    correlationId?: string;
    tags?: Record<string, string>;
    extra?: Record<string, unknown>;
  }
) {
  if (isTestEnv) return;
  if (!sentryInitialized) {
    if (import.meta.env.PROD && pendingEvents.length < MAX_PENDING) {
      pendingEvents.push({ type: "exception", error, context });
    }
    if (import.meta.env.DEV) {
      console.warn("[Sentry] ⚠️ Not initialized - queued:", error.message, "Status:", sentryStatus);
    }
    return;
  }
  if (import.meta.env.DEV) {
    console.log("[Sentry] 📤 Capturing exception:", error.message, context?.tags);
  }
  sentryModule?.captureException(error, {
    tags: context?.tags,
    extra: { ...context?.extra, correlationId: context?.correlationId },
  });
}

/**
 * Capture message (non-exception)
 */
export function captureMessage(
  message: string,
  level: 'info' | 'warning' | 'error' = 'error',
  context?: {
    correlationId?: string;
    tags?: Record<string, string>;
    extra?: Record<string, unknown>;
  }
) {
  if (isTestEnv) return;
  if (!sentryInitialized) {
    if (import.meta.env.PROD && pendingEvents.length < MAX_PENDING) {
      pendingEvents.push({ type: "message", message, level, context });
    }
    if (import.meta.env.DEV) {
      console[level]("[Sentry] Would capture message (queued):", message, context);
    }
    return;
  }
  if (!import.meta.env.PROD) {
    if (import.meta.env.DEV) {
      console[level]("[Sentry] Would capture message:", message, context);
    }
    return;
  }
  sentryModule?.captureMessage(message, {
    level,
    tags: context?.tags,
    extra: { ...context?.extra, correlationId: context?.correlationId },
  });
}

/**
 * Set user context for error tracking
 */
export function setUser(userId: string | null) {
  if (!sentryInitialized || !import.meta.env.PROD) return;
  sentryModule?.setUser(userId ? { id: userId } : null);
}

/**
 * Add breadcrumb for debugging
 */
export function addBreadcrumb(
  message: string,
  category: string,
  level: "info" | "warning" | "error" = "info",
  data?: Record<string, unknown>
) {
  if (!sentryInitialized || !import.meta.env.PROD) return;
  sentryModule?.addBreadcrumb({ message, category, level, data });
}

type SpanLike = { setAttribute?: (key: string, value: string | number | boolean) => void };

/**
 * Start a span för tracing (t.ex. ui.click, http.client).
 * När Sentry inte är initierad körs callback med en no-op span.
 */
export function startSpan<T>(
  options: { op?: string; name?: string },
  callback: (span: SpanLike) => T
): T {
  const noop: SpanLike = { setAttribute: () => {} };
  if (sentryModule?.startSpan) {
    return (sentryModule.startSpan as (o: typeof options, cb: (s: SpanLike) => T) => T)(options, callback);
  }
  return callback(noop);
}
